/**
 * PATH: apps/quote/src/components/builder/panels/context-panel/EmailsTab.tsx
 * PURPOSE: Emails tab showing linked email attachments
 */

import { useQuery } from '@tanstack/react-query'
import { Mail, Plus, Pin, Loader2, AlertCircle } from 'lucide-react'
import { quoteApi } from '@/lib/quoteApi'

interface EmailsTabProps {
  quoteId: string
  isEditable: boolean
}

export default function EmailsTab({ quoteId, isEditable }: EmailsTabProps) {
  const { data: emails = [], isLoading, error } = useQuery({
    queryKey: ['quoteAttachments', quoteId, 'email'],
    queryFn: () => quoteApi.listQuoteAttachments(quoteId, 'email'),
    staleTime: 30 * 1000,
  })

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Related Emails</h3>
        {isEditable && (
          <button
            disabled
            title="Email linking coming soon"
            className="flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 opacity-50 cursor-not-allowed"
          >
            <Plus className="w-3 h-3" /> Link
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" /> Failed to load emails
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : emails.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          <Mail className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-1" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No emails linked</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Email linking coming soon</p>
        </div>
      ) : (
        <div className="space-y-2">
          {emails.map((email) => (
            <div
              key={email.id}
              className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-slate-900 dark:text-white line-clamp-1">
                  {email.email_subject || email.title || 'No subject'}
                </p>
                {email.is_pinned && <Pin className="w-3.5 h-3.5 text-blue-500 shrink-0" />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {email.email_from || 'Unknown'} • {email.email_date || email.created_at?.split('T')[0]}
              </p>
              {email.description && (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">{email.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
